import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  clearToken,
  getAdmin,
  getToken,
  requestForm,
  requestJson,
  setAdmin,
  setToken,
} from './api';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import ProductsPage from './pages/ProductsPage';
import ProductCreatePage from './pages/ProductCreatePage';
import ProductEditPage from './pages/ProductEditPage';
import OrdersPage from './pages/OrdersPage';
import OrdersDetailPage from './pages/OrdersDetailPage';
import ClientsPage from './pages/ClientsPage';
import InventoryPage from './pages/InventoryPage';
import PricingPage from './pages/PricingPage';
import ReportsPage from './pages/ReportsPage';
import RolesPage from './pages/RolesPage';
import SettingsPage from './pages/SettingsPage';
import BannersPage from './pages/BannersPage';
import NotificationsPage from './pages/NotificationsPage';

const TOAST_DURATION_MS = 3600;
const TOAST_META = {
  success: { title: 'Success', icon: '✓' },
  error: { title: 'Error', icon: '!' },
  info: { title: 'Info', icon: 'i' },
};

const App = () => {
  const ADMIN_BASE = '/admin';
  const adminPath = (path) => `${ADMIN_BASE}${path}`;
  const [loggedIn, setLoggedIn] = useState(Boolean(getToken()));
  const [admin, setAdminState] = useState(getAdmin());
  const [login, setLogin] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [banners, setBanners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [productForm, setProductForm] = useState({
    name: '',
    brand: '',
    category: '',
    sub_category: '',
    description: '',
    main_price: '',
    discount_percentage: '',
    quantity: '',
    colors: '',
    main_image: null,
    images: [],
    variants: [],
  });
  const [productMessage, setProductMessage] = useState('');

  const [bannerForm, setBannerForm] = useState({ title: '', redirect_to: '', image: null });
  const [bannerMessage, setBannerMessage] = useState('');

  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'INFO',
    user_id: '',
  });
  const [notificationMessage, setNotificationMessage] = useState('');
  const [categoryForm, setCategoryForm] = useState({ name: '', image: null });
  const [subCategoryForm, setSubCategoryForm] = useState({ category_id: '', name: '', image: null });
  const [settingsMessage, setSettingsMessage] = useState('');
  const [toasts, setToasts] = useState([]);
  const toastTimersRef = useRef(new Map());

  const sessionName = useMemo(() => admin?.name || 'Admin', [admin]);
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timerId = toastTimersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      toastTimersRef.current.delete(id);
    }
  }, []);

  const pushToast = useCallback((text, type = 'success') => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => {
      const next = [...prev, { id, text, type }];
      if (next.length <= 4) return next;
      const trimmed = next.slice(next.length - 4);
      const trimmedIds = new Set(trimmed.map((t) => t.id));
      prev.forEach((item) => {
        if (trimmedIds.has(item.id)) return;
        const timerId = toastTimersRef.current.get(item.id);
        if (timerId) clearTimeout(timerId);
        toastTimersRef.current.delete(item.id);
      });
      return trimmed;
    });
    const timerId = setTimeout(() => {
      dismissToast(id);
    }, TOAST_DURATION_MS);
    toastTimersRef.current.set(id, timerId);
  }, [dismissToast]);

  useEffect(
    () => () => {
      toastTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      toastTimersRef.current.clear();
    },
    []
  );

  const handleLogin = async () => {
    setLoginError('');
    try {
      const data = await requestJson(
        'POST',
        '/api/accounts/admin/login/',
        { email: login.email, password: login.password },
        false
      );
      setToken(data.token);
      setAdmin(data.admin);
      setAdminState(data.admin || {});
      setLoggedIn(true);
      await loadAll();
      pushToast('Logged in successfully.');
    } catch (e) {
      const message = e.message || 'Login failed';
      setLoginError(message);
      pushToast(message, 'error');
    }
  };

  const handleLogout = () => {
    clearToken();
    setAdmin(null);
    setAdminState({});
    setLoggedIn(false);
    pushToast('Logged out.', 'info');
  };

  const loadUsers = useCallback(async () => {
    const data = await requestJson('GET', '/api/accounts/admin/users/');
    setUsers(data || []);
  }, []);

  const updateApproval = async (userId, approved) => {
    try {
      await requestJson('POST', `/api/accounts/admin/users/${userId}/approve/`, { approved });
      await loadUsers();
      pushToast(approved ? 'User approved.' : 'User marked pending.');
    } catch (e) {
      pushToast(e.message || 'Failed to update approval', 'error');
    }
  };

  const loadProducts = useCallback(async () => {
    const data = await requestJson('GET', '/api/products/', null, false);
    const items = Array.isArray(data) ? data : [];
    const toEpoch = (product) => {
      const createdAt = Date.parse(String(product?.created_at || ''));
      if (Number.isFinite(createdAt)) return createdAt;
      const id = String(product?._id || '');
      if (/^[a-f0-9]{24}$/i.test(id)) {
        return parseInt(id.slice(0, 8), 16) * 1000;
      }
      return 0;
    };
    const sorted = [...items].sort((a, b) => {
      const diff = toEpoch(b) - toEpoch(a);
      if (diff !== 0) return diff;
      return String(b?._id || '').localeCompare(String(a?._id || ''));
    });
    setProducts(sorted);
  }, []);

  const addProduct = async () => {
    setProductMessage('');
    const form = new FormData();
    Object.entries(productForm).forEach(([key, value]) => {
      if (key === 'images' || key === 'main_image' || key === 'variants') return;
      if (value !== undefined && value !== null) form.append(key, value);
    });
    if (productForm.variants?.length) {
      const variantsPayload = productForm.variants.map((variant) => ({
        ...variant,
        color_images: undefined,
        color_input: undefined,
      }));
      form.append('variants', JSON.stringify(variantsPayload));
      productForm.variants.forEach((variant, variantIndex) => {
        const colorImages = variant?.color_images || {};
        Object.entries(colorImages).forEach(([color, file]) => {
          if (!file) return;
          form.append(
            `variant_color_image_${variantIndex}__${encodeURIComponent(color)}`,
            file
          );
        });
      });
    }
    if (productForm.main_image) form.append('main_image', productForm.main_image);
    (productForm.images || []).forEach((img) => form.append('images', img));
    const directColors = String(productForm.colors || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const variantColors = (productForm.variants || []).flatMap((variant) =>
      Array.isArray(variant?.colors) ? variant.colors.map((c) => String(c || '').trim()) : []
    );
    const allColors = Array.from(new Set([...directColors, ...variantColors].filter(Boolean)));
    allColors.forEach((c) => form.append('colors', c));
    try {
      await requestForm('/api/products/', form);
      setProductMessage('Product added.');
      setProductForm({
        name: '',
        brand: '',
        category: '',
        sub_category: '',
        description: '',
        main_price: '',
        discount_percentage: '',
        quantity: '',
        colors: '',
        main_image: null,
        images: [],
        variants: [],
      });
      await loadProducts();
      pushToast('Product created.');
    } catch (e) {
      const message = e.message || 'Failed to add product';
      setProductMessage(message);
      pushToast(message, 'error');
    }
  };

  const addStock = async (productId, quantity) => {
    try {
      await requestJson('POST', `/api/products/${productId}/add-stock/`, { quantity });
      await loadProducts();
      pushToast('Stock updated.');
    } catch (e) {
      pushToast(e.message || 'Failed to add stock', 'error');
    }
  };

  const updateProduct = async (productId, payload) => {
    try {
      if (payload instanceof FormData) {
        await requestForm(`/api/products/${productId}/update/`, payload, true, 'PUT');
      } else {
        await requestJson('PUT', `/api/products/${productId}/update/`, payload);
      }
      await loadProducts();
      pushToast('Product updated.');
    } catch (e) {
      pushToast(e.message || 'Failed to update product', 'error');
      throw e;
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await requestJson('DELETE', `/api/products/${productId}/delete/`);
      await loadProducts();
      pushToast('Product deleted.');
    } catch (e) {
      pushToast(e.message || 'Failed to delete product', 'error');
    }
  };

  const loadOrders = useCallback(async () => {
    const data = await requestJson('GET', '/api/orders/admin/all/');
    setOrders(data || []);
  }, []);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await requestJson('POST', `/api/orders/admin/${orderId}/status/`, { status });
      await loadOrders();
      pushToast(`Order status updated to ${status}.`);
    } catch (e) {
      pushToast(e.message || 'Failed to update order status', 'error');
    }
  };

  const cancelOrder = async (orderId, reason) => {
    try {
      await requestJson('POST', `/api/orders/admin/${orderId}/cancel/`, { reason });
      await loadOrders();
      pushToast('Order cancelled.');
    } catch (e) {
      pushToast(e.message || 'Failed to cancel order', 'error');
      throw e;
    }
  };

  const loadBanners = useCallback(async () => {
    const data = await requestJson('GET', '/api/banners/', null, false);
    setBanners(data || []);
  }, []);

  const addBanner = async () => {
    setBannerMessage('');
    const form = new FormData();
    form.append('title', bannerForm.title);
    form.append('redirect_to', bannerForm.redirect_to);
    if (bannerForm.image) form.append('image', bannerForm.image);
    try {
      await requestForm('/api/banners/', form);
      setBannerMessage('Banner added.');
      setBannerForm({ title: '', redirect_to: '', image: null });
      await loadBanners();
      pushToast('Banner created.');
    } catch (e) {
      const message = e.message || 'Failed to add banner';
      setBannerMessage(message);
      pushToast(message, 'error');
    }
  };

  const updateBanner = async (bannerId) => {
    setBannerMessage('');
    const form = new FormData();
    form.append('title', bannerForm.title || '');
    form.append('redirect_to', bannerForm.redirect_to || '');
    if (bannerForm.image) form.append('image', bannerForm.image);
    try {
      await requestForm(`/api/banners/${bannerId}/`, form, true, 'PUT');
      setBannerMessage('Banner updated.');
      setBannerForm({ title: '', redirect_to: '', image: null });
      await loadBanners();
      pushToast('Banner updated.');
    } catch (e) {
      const message = e.message || 'Failed to update banner';
      setBannerMessage(message);
      pushToast(message, 'error');
    }
  };

  const deleteBanner = async (bannerId) => {
    setBannerMessage('');
    try {
      await requestJson('DELETE', `/api/banners/${bannerId}/`);
      setBannerMessage('Banner removed.');
      await loadBanners();
      pushToast('Banner removed.');
    } catch (e) {
      const message = e.message || 'Failed to remove banner';
      setBannerMessage(message);
      pushToast(message, 'error');
    }
  };

  const loadNotifications = useCallback(async () => {
    const data = await requestJson('GET', '/api/notifications/');
    setNotifications(data.items || []);
  }, []);

  const loadCategorySettings = useCallback(async () => {
    const data = await requestJson('GET', '/api/home/categories/', null, false);
    setCategories(data || []);
  }, []);

  const loadSubCategorySettings = useCallback(async () => {
    const data = await requestJson('GET', '/api/home/sub-categories/', null, false);
    setSubCategories(data || []);
  }, []);

  const addCategorySetting = async () => {
    setSettingsMessage('');
    const form = new FormData();
    form.append('name', categoryForm.name || '');
    if (categoryForm.image) form.append('image', categoryForm.image);
    try {
      await requestForm('/api/home/categories/create/', form);
      setSettingsMessage('Category added.');
      setCategoryForm({ name: '', image: null });
      await loadCategorySettings();
      pushToast('Category created.');
    } catch (e) {
      const message = e.message || 'Failed to add category';
      setSettingsMessage(message);
      pushToast(message, 'error');
    }
  };

  const addSubCategorySetting = async () => {
    setSettingsMessage('');
    const form = new FormData();
    form.append('category_id', subCategoryForm.category_id || '');
    form.append('name', subCategoryForm.name || '');
    if (subCategoryForm.image) form.append('image', subCategoryForm.image);
    try {
      await requestForm('/api/home/sub-categories/create/', form);
      setSettingsMessage('Sub category added.');
      setSubCategoryForm({ category_id: '', name: '', image: null });
      await loadSubCategorySettings();
      pushToast('Sub category created.');
    } catch (e) {
      const message = e.message || 'Failed to add sub category';
      setSettingsMessage(message);
      pushToast(message, 'error');
    }
  };

  const addNotification = async () => {
    setNotificationMessage('');
    try {
      await requestJson('POST', '/api/notifications/create/', {
        title: notificationForm.title,
        message: notificationForm.message,
        type: notificationForm.type || 'INFO',
        user_id: notificationForm.user_id || undefined,
      });
      setNotificationMessage('Notification sent.');
      setNotificationForm({ title: '', message: '', type: 'INFO', user_id: '' });
      await loadNotifications();
      pushToast('Notification sent.');
    } catch (e) {
      const message = e.message || 'Failed to send';
      setNotificationMessage(message);
      pushToast(message, 'error');
    }
  };

  const loadAll = useCallback(async () => {
    await Promise.all([
      loadUsers(),
      loadProducts(),
      loadOrders(),
      loadBanners(),
      loadNotifications(),
      loadCategorySettings(),
      loadSubCategorySettings(),
    ]);
  }, [
    loadUsers,
    loadProducts,
    loadOrders,
    loadBanners,
    loadNotifications,
    loadCategorySettings,
    loadSubCategorySettings,
  ]);

  useEffect(() => {
    if (loggedIn) loadAll();
  }, [loggedIn, loadAll]);

  return (
    <Layout
      loggedIn={loggedIn}
      sessionName={sessionName}
      handleLogout={handleLogout}
    >
      <Routes>
        <Route
          path={adminPath('/login')}
          element={
            loggedIn ? (
              <Navigate to={adminPath('/users')} replace />
            ) : (
              <LoginPage
                login={login}
                setLogin={setLogin}
                handleLogin={handleLogin}
                loginError={loginError}
              />
            )
          }
        />
        <Route
          path={adminPath('/dashboard')}
          element={
            loggedIn ? (
              <DashboardPage orders={orders} products={products} users={users} />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/users')}
          element={
            loggedIn ? (
              <UsersPage users={users} loadUsers={loadUsers} updateApproval={updateApproval} />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/products')}
          element={
            loggedIn ? (
              <ProductsPage
                products={products}
                loadProducts={loadProducts}
                addStock={addStock}
                deleteProduct={deleteProduct}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/products/new')}
          element={
            loggedIn ? (
              <ProductCreatePage
                productForm={productForm}
                setProductForm={setProductForm}
                addProduct={addProduct}
                productMessage={productMessage}
                categories={categories}
                subCategories={subCategories}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/products/edit/:id')}
          element={
            loggedIn ? (
              <ProductEditPage
                updateProduct={updateProduct}
                categories={categories}
                subCategories={subCategories}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/orders')}
          element={
            loggedIn ? (
              <OrdersPage
                orders={orders}
                loadOrders={loadOrders}
                updateOrderStatus={updateOrderStatus}
                cancelOrder={cancelOrder}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/orders/:id')}
          element={
            loggedIn ? (
              <OrdersDetailPage />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/clients')}
          element={
            loggedIn ? (
              <ClientsPage />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/inventory')}
          element={
            loggedIn ? (
              <InventoryPage />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/pricing')}
          element={
            loggedIn ? (
              <PricingPage />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/reports')}
          element={
            loggedIn ? (
              <ReportsPage orders={orders} products={products} users={users} />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/roles')}
          element={
            loggedIn ? (
              <RolesPage />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/settings')}
          element={
            loggedIn ? (
              <SettingsPage
                categories={categories}
                subCategories={subCategories}
                categoryForm={categoryForm}
                setCategoryForm={setCategoryForm}
                subCategoryForm={subCategoryForm}
                setSubCategoryForm={setSubCategoryForm}
                addCategorySetting={addCategorySetting}
                addSubCategorySetting={addSubCategorySetting}
                settingsMessage={settingsMessage}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/banners')}
          element={
            loggedIn ? (
              <BannersPage
                bannerForm={bannerForm}
                setBannerForm={setBannerForm}
                addBanner={addBanner}
                updateBanner={updateBanner}
                deleteBanner={deleteBanner}
                bannerMessage={bannerMessage}
                banners={banners}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path={adminPath('/notifications')}
          element={
            loggedIn ? (
              <NotificationsPage
                notificationForm={notificationForm}
                setNotificationForm={setNotificationForm}
                addNotification={addNotification}
                notificationMessage={notificationMessage}
                notifications={notifications}
              />
            ) : (
              <Navigate to={adminPath('/login')} replace />
            )
          }
        />
        <Route
          path="/"
          element={<Navigate to={loggedIn ? adminPath('/dashboard') : adminPath('/login')} replace />}
        />
        <Route
          path={ADMIN_BASE}
          element={<Navigate to={loggedIn ? adminPath('/dashboard') : adminPath('/login')} replace />}
        />
        <Route
          path="*"
          element={<Navigate to={loggedIn ? adminPath('/dashboard') : adminPath('/login')} replace />}
        />
      </Routes>
      <div className="toast-stack" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            style={{ '--toast-duration': `${TOAST_DURATION_MS}ms` }}
            role="status"
          >
            <div className={`toast-icon toast-icon-${toast.type}`}>
              {(TOAST_META[toast.type] || TOAST_META.info).icon}
            </div>
            <div className="toast-body">
              <div className="toast-title">{(TOAST_META[toast.type] || TOAST_META.info).title}</div>
              <div className="toast-text">{toast.text}</div>
              <div className="toast-progress" />
            </div>
            <button
              type="button"
              className="toast-close"
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss notification"
            >
              x
            </button>
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default App;
